import { useEffect, useState } from 'react'
import { api, type AdminNewsletter } from './api'
import Pager from './Pager'
import StatTile from './StatTile'
import ViewsDayChart from './ViewsDayChart'

function formatDate(iso: string | null) {
  if (!iso) return ''
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Mailchimp's member "source", in words an editor recognises. */
function sourceLabel(source: string) {
  if (/^api/i.test(source)) return 'Website form'
  if (/signup form|hosted/i.test(source)) return 'Mailchimp form'
  if (/import/i.test(source)) return 'Import'
  if (/admin/i.test(source)) return 'Added in Mailchimp'
  return source || '—'
}

const SIGNUPS_PER_PAGE = 10

function rate(value: number | null) {
  return value == null ? '—' : `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
}

export default function AdminNewsletterPage() {
  const [data, setData] = useState<AdminNewsletter | null>(null)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    api<AdminNewsletter>('/api/admin/newsletter')
      .then(setData)
      .catch((err: Error) => setError(err.message))
  }, [])

  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-caps text-[10px] text-rose">Audience</p>
          <h2 className="mt-1 font-serif text-[32px] leading-none font-medium">Newsletter</h2>
        </div>
        {data?.configured ? (
          <a
            href={data.mailchimpUrl}
            target="_blank"
            rel="noreferrer"
            className="text-caps inline-flex h-9 items-center bg-ink px-4 text-white hover:bg-rose"
          >
            Open in Mailchimp
          </a>
        ) : null}
      </div>
      <p className="mt-3 max-w-[56ch] text-sm text-muted">
        Live from your Mailchimp audience. New sign-ups include the site form and anyone added in Mailchimp.
      </p>

      {error ? <p className="mt-6 text-sm text-rose">{error}</p> : null}
      {!error && !data ? <p className="mt-6 text-muted">Loading Mailchimp numbers…</p> : null}
      {data && !data.configured ? (
        <p className="mt-6 text-sm text-muted">
          Mailchimp is not connected yet. Set MAILCHIMP_API_KEY and MAILCHIMP_AUDIENCE_ID in Railway.
        </p>
      ) : null}

      {data?.configured ? (
        <>
          <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-3">
            <StatTile label="Subscribers" value={data.total} note="Everyone on the audience" />
            <StatTile
              label="New · 7 days"
              value={data.week.new}
              note={`${data.previousWeek.new.toLocaleString()} the 7 days before`}
            />
            <StatTile label="New · 30 days" value={data.month.new} />
            <StatTile label="Unsubscribed · 30 days" value={data.month.unsubscribed} />
            <StatTile
              label="Average opens"
              value={data.openRate ?? 0}
              suffix="%"
              decimals={1}
              note={data.campaignCount ? `Across ${data.campaignCount.toLocaleString()} emails` : 'No emails sent yet'}
            />
            <StatTile label="Average clicks" value={data.clickRate ?? 0} suffix="%" decimals={1} />
          </div>

          <div className="mt-10">
            <h3 className="text-caps text-[11px] text-muted">New sign-ups per day · last 30 days</h3>
            <ViewsDayChart days={data.days} heightClass="h-32" minBar={2} />
          </div>

          <div className="mt-12 grid gap-10 xl:grid-cols-[3fr_2fr]">
            <div className="min-w-0">
              <h3 className="text-caps text-[11px] text-muted">Latest sign-ups</h3>
              {data.recent.length === 0 ? (
                <p className="mt-4 text-sm text-muted">No subscribers yet.</p>
              ) : (
                <ul className="mt-4 divide-y divide-rule border-y border-rule">
                  {data.recent.slice((page - 1) * SIGNUPS_PER_PAGE, page * SIGNUPS_PER_PAGE).map((member) => (
                    <li key={member.email} className="flex items-baseline justify-between gap-4 py-2.5">
                      <div className="min-w-0">
                        <p className="truncate text-[15px]">{member.name || member.email}</p>
                        {member.name ? <p className="truncate text-[12px] text-muted">{member.email}</p> : null}
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-[12px] text-muted">{formatDate(member.joinedAt)}</p>
                        <p className="text-[12px] text-muted">{sourceLabel(member.source)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <Pager
                page={page}
                pages={Math.ceil(data.recent.length / SIGNUPS_PER_PAGE)}
                onPage={setPage}
              />
            </div>

            <div className="min-w-0">
              <h3 className="text-caps text-[11px] text-muted">Recent emails</h3>
              {data.campaigns.length === 0 ? (
                <p className="mt-4 text-sm text-muted">No emails sent to this audience yet.</p>
              ) : (
                <ul className="mt-4 divide-y divide-rule border-y border-rule">
                  {data.campaigns.map((campaign) => (
                    <li key={campaign.id} className="py-2.5">
                      <p className="text-[15px] leading-snug">{campaign.subject}</p>
                      <p className="mt-1 text-[12px] text-muted">
                        {formatDate(campaign.sentAt)} · {campaign.recipients.toLocaleString()} sent · {rate(campaign.openRate)} opened ·{' '}
                        {rate(campaign.clickRate)} clicked
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      ) : null}
    </section>
  )
}
